import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LanguageCode } from '../../../shared/types';
import { useAvailableLanguages, useSwitchLanguage } from '../hooks/useLanguage.js';
import { LanguageSelector } from '../components/language/LanguageSelector.js';
import { Button } from '../components/common/Button.js';
import { LoadingSpinner } from '../components/common/LoadingSpinner.js';

export function LanguageSelection() {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode | null>(null);

  const { languages, isLoading: isLoadingLanguages, error: languagesError } = useAvailableLanguages();
  const { switchLanguage, isLoading: isSwitching, error: switchError } = useSwitchLanguage();

  const handleContinue = async () => {
    if (!selectedLanguage) return;

    try {
      await switchLanguage(selectedLanguage);
      // Navigate to dashboard after successful language selection
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to set language:', error);
    }
  };

  if (isLoadingLanguages) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-[var(--color-muted)]">Loading languages...</p>
        </div>
      </div>
    );
  }

  if (languagesError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-error-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">Error Loading Languages</h2>
          <p className="text-[var(--color-muted)] mb-6">{languagesError}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-[var(--color-bg)] to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-semibold text-[var(--color-text)] mb-4">
            Choose Your Learning Language
          </h1>
          <p className="text-lg text-[var(--color-muted)] max-w-2xl mx-auto">
            Select the language you want to learn. You can always change this later in your
            settings, and your progress in each language will be saved separately.
          </p>
        </div>

        {/* Language Selector */}
        <div className="mb-8">
          <LanguageSelector
            languages={languages}
            selectedLanguage={selectedLanguage}
            onSelect={setSelectedLanguage}
            isLoading={isLoadingLanguages}
            showDescriptions={true}
            layout="grid"
          />
        </div>

        {/* Error Message */}
        {switchError && (
          <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-xl">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-error-500 mt-0.5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-error-700">Failed to set language</h3>
                <p className="text-sm text-error-600 mt-1">{switchError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          <Button
            variant="primary"
            size="lg"
            onClick={handleContinue}
            disabled={!selectedLanguage || isSwitching}
            isLoading={isSwitching}
            className="min-w-[200px]"
          >
            {isSwitching ? 'Setting up...' : 'Continue'}
          </Button>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-[var(--color-muted)]">
            Not sure which to choose? You can explore all languages and switch between them anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
